import React, { useEffect } from 'react';
import ActionButtons from './ActionButtons';
import Select from '../../../../Select/Select';
import StepperInput from '../../../../StepperInput/StepperInput';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { parseFileSizeConditions } from './FilterUtils';
import { FILE_SIZE_FORMATS_OPTIONS, FILTER_OPERATORS } from '../../../utils/constants';

interface FileSizeFilterDropdownProps {
  closeDropdown: () => void;
  
}

interface SelectOption {
  label: string;
  value: string;
}

const formatToBytes = (value: number, format: string): number => {
  switch (format) {
    case 'kb':
      return value * 1024;
    case 'mb':
      return value * 1024 * 1024;
    case 'gb':
      return value * 1024 * 1024 * 1024;
    default:
      return value; // assume value is already in bytes
  }
};


const FileSizeFilterDropdown: React.FC<FileSizeFilterDropdownProps> = ({ closeDropdown }) => {
  const [fileSize, setFileSize] = React.useState<{
    min: number | null;
    max: number | null;
  }>({ min: null, max: null });

  const [minDisplay, setMinDisplay] = React.useState<number | undefined>(undefined);
  const [maxDisplay, setMaxDisplay] = React.useState<number | undefined>(undefined);

  const selectOptions: SelectOption[] = FILE_SIZE_FORMATS_OPTIONS

  const [fileSizeFormat, setFileSizeFormat] = React.useState<SelectOption>(selectOptions[1]);

  const { setCondition, removeCondition, getConditionValue } = useQueryBuilder();

  const clearAll = () => {
    setMinDisplay(undefined);
    setMaxDisplay(undefined);
    setFileSize({ min: null, max: null });
    setFileSizeFormat(selectOptions[1]);
  };

  const applyFilter = () => {
    if (minDisplay === undefined && maxDisplay === undefined) {
      removeCondition('file_size');
      closeDropdown();
      return;
    }

    const minBytes = formatToBytes(minDisplay ?? 0, fileSizeFormat.value);
    const maxBytes = formatToBytes(maxDisplay ?? 0, fileSizeFormat.value);
    setFileSize({ min: minBytes, max: maxBytes });

    setCondition('file_size', FILTER_OPERATORS.RANGE, {
      $lte: maxBytes,
      $gte: minBytes,
    });

    closeDropdown();
  };


  const handleMinChange = (value: number | null) => {
    setMinDisplay(value ?? undefined);
  };

  const handleMaxChange = (value: number | null) => {
    setMaxDisplay(value ?? undefined);
  };

  const handleDropdownChange = (e: SelectOption) => {
    setFileSizeFormat(e);
  };

  useEffect(() => {
    const condition = getConditionValue('file_size') ?? {};
    const conditions = condition?.[FILTER_OPERATORS.RANGE] ?? {};

    const {
      format,
      minDisplayValue,
      maxDisplayValue,
      fileSize,
    } = parseFileSizeConditions(conditions, selectOptions);

    setFileSizeFormat(format);
    setMinDisplay(minDisplayValue!);
    setMaxDisplay(maxDisplayValue!);
    setFileSize({
      min: fileSize.min ?? null,
      max: fileSize.max ?? null,
    });
  }, []);

  return (
    <div className="FileSizeFilter__container">
      <div className="FileSizeFilter__header"><b>{'File Size Limit'}</b></div>
      <div className="FileSizeFilter">
        <StepperInput
          min={0}
          max={999999999}
          step={1}
          value={minDisplay}
          onChange={handleMinChange}
          placeholder={'Min'}
          inputWidth="3.5rem"
        />
        <StepperInput
          min={0}
          max={999999999}
          step={1}
          value={maxDisplay}
          onChange={handleMaxChange}
          placeholder={'Max'}
          inputWidth="3.5rem"
        />
        <Select
          className="FileSizeFilter__format-dropdown"
          version="v2"
          options={selectOptions}
          value={fileSizeFormat}
          isSearchable={false}
          onChange={handleDropdownChange}
          menuShouldScrollIntoView={false}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
          }}
          menuPortalTarget={document.body}
        />
      </div>
      <div>
        <ActionButtons
          closeDropdown={closeDropdown}
          clearAll={clearAll}
          applyFilter={applyFilter}
        />
      </div>
    </div>
  );
};

export default FileSizeFilterDropdown;
